import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * One class session of a feedback's period, as computed now by the server
 * (`buildSessionsForPeriod`): presence and homework status for that class.
 */
export type FeedbackSession = {
  eventId: string;
  eventDate: Date | string;
  title: string | null;
  classId: string | null;
  className: string | null;
  /** null = no attendance record for the session. */
  isPresent: boolean | null;
  homework: {
    status: 'upcoming' | 'no-exercises' | 'none' | 'partial' | 'full';
    submittedExercises: number;
    totalExercises: number;
  } | null;
};

/** Where a dot should take the reader (the class page), or null for a plain dot. */
export type SessionHref = (session: FeedbackSession) => string | null;

const GREEN = '#4aba6a';
const ORANGE = '#f0a030';
const RED = '#e24b4a';
const GRAY = '#cbd5e1';

const formatDate = (value: Date | string, locale: string) => {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString(locale, { day: '2-digit', month: 'short' });
};

type DotStyle = { color: string; hollow: boolean };

export function presenceDotStyle(session: FeedbackSession): DotStyle {
  if (session.isPresent === true) return { color: GREEN, hollow: false };
  if (session.isPresent === false) return { color: RED, hollow: false };
  return { color: GRAY, hollow: true };
}

export function homeworkDotStyle(session: FeedbackSession): DotStyle {
  const status = session.homework?.status;
  if (status === 'full') return { color: GREEN, hollow: false };
  if (status === 'partial') return { color: ORANGE, hollow: false };
  if (status === 'none') return { color: RED, hollow: true };
  return { color: GRAY, hollow: true };
}

function DotGrid({
  sessions,
  kind,
  classHref,
  ariaLabel,
}: {
  sessions: FeedbackSession[];
  kind: 'presence' | 'homework';
  classHref?: SessionHref;
  ariaLabel: string;
}) {
  const { t, i18n } = useTranslation('admin');
  const columns = Math.max(1, Math.ceil(sessions.length / 2));

  const titleFor = (session: FeedbackSession) => {
    const date = formatDate(session.eventDate, i18n.language);
    const className = session.className?.trim() || session.title?.trim() || t('studentFeedback.untitledClass');
    if (kind === 'presence') {
      if (session.isPresent === true) return t('studentFeedback.presenceDotPresent', { date, className });
      if (session.isPresent === false) return t('studentFeedback.presenceDotAbsent', { date, className });
      return t('studentFeedback.presenceDotNoRecord', { date, className });
    }
    const homework = session.homework;
    if (!homework || homework.status === 'no-exercises' || homework.status === 'upcoming') {
      return t('studentFeedback.homeworkDotNoExercises', { date, className });
    }
    const counts = { date, className, submitted: homework.submittedExercises, total: homework.totalExercises };
    if (homework.status === 'full') return t('studentFeedback.homeworkDotFull', counts);
    if (homework.status === 'partial') return t('studentFeedback.homeworkDotPartial', counts);
    return t('studentFeedback.homeworkDotNone', counts);
  };

  return (
    <ol
      className="m-0 grid w-full list-none gap-1.5 p-0"
      style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}
      aria-label={ariaLabel}
    >
      {sessions.map((session) => {
        const { color, hollow } = kind === 'presence' ? presenceDotStyle(session) : homeworkDotStyle(session);
        const title = titleFor(session);
        const href = classHref?.(session) ?? null;
        const dot = (
          <span
            data-testid={`${kind}-dot`}
            data-status={kind === 'presence' ? String(session.isPresent) : session.homework?.status ?? 'none'}
            className="block aspect-square w-full rounded-full border-2 transition group-hover:scale-110"
            style={{ maxWidth: 16, borderColor: color, backgroundColor: hollow ? 'transparent' : color }}
          />
        );
        return (
          <li key={session.eventId} className="flex justify-center">
            {href ? (
              <a
                href={href}
                title={title}
                aria-label={title}
                className="group block w-full max-w-[16px] rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-brand"
              >
                {dot}
              </a>
            ) : (
              <span title={title} aria-label={title} role="img" className="block w-full max-w-[16px]">
                {dot}
              </span>
            )}
          </li>
        );
      })}
    </ol>
  );
}

function StatBlock({ headline, label, children }: { headline: ReactNode; label: ReactNode; children: ReactNode }) {
  return (
    <div className="flex w-[200px] flex-col items-center gap-3 sm:w-[220px]">
      <div className="text-center leading-tight">
        <div className="text-2xl font-semibold tabular-nums text-gray-900">{headline}</div>
        <div className="text-xs font-medium text-gray-900 sm:text-[13px]">{label}</div>
      </div>
      {children}
    </div>
  );
}

/**
 * Attendance as one dot per class of the period: green present, red absent,
 * hollow gray when the class has no attendance record yet.
 */
export function PresenceDots({ sessions, classHref }: { sessions: FeedbackSession[]; classHref?: SessionHref }) {
  const { t } = useTranslation('admin');
  const recorded = sessions.filter((session) => session.isPresent !== null);
  const present = recorded.filter((session) => session.isPresent).length;
  return (
    <StatBlock
      headline={t('studentFeedback.statPresenceClasses', { present, total: recorded.length })}
      label={t('studentFeedback.statPresenceClassesLabel')}
    >
      <DotGrid sessions={sessions} kind="presence" classHref={classHref} ariaLabel={t('studentFeedback.statPresence')} />
    </StatBlock>
  );
}

/**
 * Homework as one dot per class of the period: green when a whole section was
 * done, orange when something was done, hollow red when nothing, gray when the
 * class has no activities. Each dot opens the class when `classHref` is given.
 */
export function HomeworkDots({ sessions, classHref }: { sessions: FeedbackSession[]; classHref?: SessionHref }) {
  const { t } = useTranslation('admin');
  const withExercises = sessions.filter(
    (session) => session.homework && session.homework.status !== 'no-exercises' && session.homework.status !== 'upcoming',
  );
  const done = withExercises.filter((session) => session.homework!.status !== 'none').length;
  return (
    <StatBlock
      headline={t('studentFeedback.statHomeworkClasses', { done, total: withExercises.length })}
      label={t('studentFeedback.statHomeworkClassesLabel')}
    >
      <DotGrid sessions={withExercises} kind="homework" classHref={classHref} ariaLabel={t('studentFeedback.statSubmissions')} />
    </StatBlock>
  );
}
